/**
 * Admin2FA.js
 * ===========
 *
 * Components for Admin 2FA:
 * 1. Setup2FAModal - Initial setup with QR code
 * 2. Verify2FAModal - Verification on admin access
 * 3. Admin2FASettings - Settings panel for enabling/disabling
 *
 * Location: frontend/src/components/Admin2FA.js
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Shield,
  Smartphone,
  Key,
  CheckCircle,
  XCircle,
  Loader,
  Copy,
  X,
  AlertTriangle,
  Lock
} from 'lucide-react';
import { showToast } from './Toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';


// ==========================================
// SETUP 2FA MODAL
// ==========================================

export const Setup2FAModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: show QR, 2: verify code
  const [loading, setLoading] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      generateSecret();
    }
  }, [isOpen]);

  useEffect(() => {
    if (step === 2 && inputRef.current) {
      inputRef.current.focus();
    }
  }, [step]);

  const generateSecret = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/2fa/setup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setQrCode(data.qr_code);
        setSecret(data.secret);
        setStep(1);
      } else {
        const err = await response.json();
        const errorMsg = typeof err.detail === 'string'
          ? err.detail
          : Array.isArray(err.detail)
            ? err.detail[0]?.msg || 'Validation error'
            : 'Failed to generate 2FA secret';
        setError(errorMsg);
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnable = async () => {
    if (verifyCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/2fa/verify?code=${verifyCode}`, {
        method: 'POST',
        headers: {
        'Authorization': `Bearer ${token}`
      }
    });

      if (response.ok) {
        showToast('2FA enabled successfully!', 'success');
        onSuccess?.();
        onClose();
      } else {
        const err = await response.json();
        const errorMsg = typeof err.detail === 'string'
          ? err.detail
          : Array.isArray(err.detail)
            ? err.detail[0]?.msg || 'Validation error'
            : 'Invalid code. Please try again.';
        setError(errorMsg);
        setVerifyCode('');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    showToast('Secret copied to clipboard', 'success');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Shield size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold">Enable 2FA</h2>
                <p className="text-blue-100 text-sm">Secure your admin account</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading && step === 1 && !qrCode ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="animate-spin h-8 w-8 text-blue-600" />
            </div>
          ) : step === 1 ? (
            <>
              {/* Step 1: Show QR Code */}
              <div className="text-center mb-6">
                <p className="text-gray-600 mb-4">
                  Scan this QR code with Google Authenticator or any TOTP app
                </p>

                {qrCode && (
                  <div className="inline-block p-4 bg-white border-2 border-gray-200 rounded-xl mb-4">
                    <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
                  </div>
                )}

                <div className="text-sm text-gray-500 mb-4">
                  Or enter this key manually:
                </div>

                <div className="flex items-center justify-center space-x-2 mb-6">
                  <code className="bg-gray-100 px-4 py-2 rounded-lg font-mono text-sm">
                    {secret}
                  </code>
                  <button
                    onClick={copySecret}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  >
                    <Copy size={18} />
                  </button>
                </div>
              </div>

              <button
                onClick={() => setStep(2)}
                className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
              >
                I've scanned the code → Next
              </button>
            </>
          ) : (
            <>
              {/* Step 2: Verify Code */}
              <div className="text-center mb-6">
                <Smartphone className="mx-auto text-blue-600 mb-4" size={48} />
                <p className="text-gray-600 mb-4">
                  Enter the 6-digit code from your authenticator app
                </p>

                <input
                  ref={inputRef}
                  type="text"
                  value={verifyCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setVerifyCode(val);
                    setError('');
                  }}
                  placeholder="000000"
                  className="w-full text-center text-3xl font-mono tracking-[0.5em] py-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                  maxLength={6}
                />

                {error && (
                  <p className="text-red-500 text-sm mt-2 flex items-center justify-center">
                    <AlertTriangle size={14} className="mr-1" />
                    {error}
                  </p>
                )}
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                >
                  ← Back
                </button>
                <button
                  onClick={verifyAndEnable}
                  disabled={loading || verifyCode.length !== 6}
                  className="flex-1 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {loading ? (
                    <Loader className="animate-spin h-5 w-5 mx-auto" />
                  ) : (
                    'Enable 2FA'
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};


// ==========================================
// VERIFY 2FA MODAL (shown on admin access)
// ==========================================

export const Verify2FAModal = ({ isOpen, onSuccess, onCancel }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleVerify = async () => {
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/2fa/verify?code=${code}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

      if (response.ok) {
        const data = await response.json();
        // Store session ID
        sessionStorage.setItem('admin_2fa_session', data.session_id);
        onSuccess?.();
      } else {
        const err = await response.json();
        const errorMsg = typeof err.detail === 'string'
          ? err.detail
          : Array.isArray(err.detail)
            ? err.detail[0]?.msg || 'Validation error'
            : 'Invalid code';
        setError(errorMsg);
        setCode('');
      }
    } catch (error) {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  // Auto-submit when 6 digits entered
  useEffect(() => {
    if (code.length === 6) {
      handleVerify();
    }
  }, [code]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="text-blue-600" size={32} />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Admin Verification</h2>
          <p className="text-gray-600 text-sm mt-1">
            Enter your 2FA code to access admin panel
          </p>
        </div>

        <input
          ref={inputRef}
          type="text"
          value={code}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 6);
            setCode(val);
            setError('');
          }}
          placeholder="000000"
          className="w-full text-center text-3xl font-mono tracking-[0.5em] py-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none mb-4"
          maxLength={6}
          autoComplete="one-time-code"
        />

        {error && (
          <p className="text-red-500 text-sm mb-4 flex items-center justify-center">
            <AlertTriangle size={14} className="mr-1" />
            {error}
          </p>
        )}

        {loading && (
          <div className="flex items-center justify-center py-2">
            <Loader className="animate-spin h-6 w-6 text-blue-600" />
          </div>
        )}

        <button
          onClick={onCancel}
          className="w-full py-2 text-gray-500 hover:text-gray-700 text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};


// ==========================================
// ADMIN 2FA SETTINGS PANEL
// ==========================================

export const Admin2FASettings = ({ user, onUpdate }) => {
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if 2FA is enabled for user
    check2FAStatus();
  }, []);

  const check2FAStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setIs2FAEnabled(data.admin_2fa_enabled || false);
      }
    } catch (error) {
      console.error('Failed to check 2FA status');
    }
  };

  const handleDisable2FA = async () => {
    if (disableCode.length !== 6) {
      showToast('Please enter a 6-digit code', 'warning');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/admin/2fa/disable?code=${disableCode}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

      if (response.ok) {
        showToast('2FA disabled', 'success');
        setIs2FAEnabled(false);
        setShowDisable(false);
        setDisableCode('');
        onUpdate?.();
      } else {
        const err = await response.json();
        const errorMsg = typeof err.detail === 'string'
          ? err.detail
          : Array.isArray(err.detail)
            ? err.detail[0]?.msg || 'Validation error'
            : 'Invalid code';
        showToast(errorMsg, 'error');
      }
    } catch (error) {
      showToast('Connection error', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-lg ${is2FAEnabled ? 'bg-green-100' : 'bg-gray-100'}`}>
            <Shield className={is2FAEnabled ? 'text-green-600' : 'text-gray-500'} size={24} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Two-Factor Authentication</h3>
            <p className="text-sm text-gray-500">
              {is2FAEnabled ? 'Your admin account is protected' : 'Add an extra layer of security'}
            </p>
          </div>
        </div>

        {is2FAEnabled ? (
          <div className="flex items-center space-x-2">
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium flex items-center">
              <CheckCircle size={14} className="mr-1" />
              Enabled
            </span>
            <button
              onClick={() => setShowDisable(true)}
              className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium"
            >
              Disable
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowSetup(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
          >
            Enable 2FA
          </button>
        )}
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <p className="font-medium mb-1">Why enable 2FA?</p>
        <ul className="list-disc list-inside space-y-1 text-blue-700">
          <li>Protects your admin account from unauthorized access</li>
          <li>Required for sensitive operations like user management</li>
          <li>Works with Google Authenticator, Authy, or any TOTP app</li>
        </ul>
      </div>

      {/* Setup Modal */}
      <Setup2FAModal
        isOpen={showSetup}
        onClose={() => setShowSetup(false)}
        onSuccess={() => {
          setIs2FAEnabled(true);
          onUpdate?.();
        }}
      />

      {/* Disable Confirmation Modal */}
      {showDisable && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
            <div className="text-center mb-4">
              <AlertTriangle className="mx-auto text-yellow-500 mb-2" size={48} />
              <h3 className="text-lg font-bold">Disable 2FA?</h3>
              <p className="text-gray-600 text-sm">
                This will make your admin account less secure
              </p>
            </div>

            <input
              type="text"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter 2FA code"
              className="w-full text-center text-2xl font-mono tracking-widest py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none mb-4"
              maxLength={6}
            />

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDisable(false);
                  setDisableCode('');
                }}
                className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDisable2FA}
                disabled={loading || disableCode.length !== 6}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg font-medium disabled:opacity-50"
              >
                {loading ? <Loader className="animate-spin h-5 w-5 mx-auto" /> : 'Disable'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ==========================================
// HOC: Require 2FA for Admin Routes
// ==========================================

export const withAdmin2FA = (WrappedComponent) => {
  return function Admin2FAWrapper(props) {
    const [verified, setVerified] = useState(false);
    const [checking, setChecking] = useState(true);
    const [needs2FA, setNeeds2FA] = useState(false);

    useEffect(() => {
      checkAdmin2FA();
    }, []);

    const checkAdmin2FA = async () => {
      // Check if already verified in this session
      const sessionId = sessionStorage.getItem('admin_2fa_session');
      if (sessionId) {
        setVerified(true);
        setChecking(false);
        return;
      }

      // Check if user has 2FA enabled
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.admin_2fa_enabled) {
            setNeeds2FA(true);
          } else {
            setVerified(true);
          }
        }
      } catch (error) {
        console.error('2FA check failed');
        setVerified(true); // Allow access on error
      } finally {
        setChecking(false);
      }
    };

    if (checking) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <Loader className="animate-spin h-8 w-8 text-blue-600" />
        </div>
      );
    }

    if (needs2FA && !verified) {
      return (
        <Verify2FAModal
          isOpen={true}
          onSuccess={() => setVerified(true)}
          onCancel={() => window.location.href = '/'}
        />
      );
    }

    return <WrappedComponent {...props} />;
  };
};

export default {
  Setup2FAModal,
  Verify2FAModal,
  Admin2FASettings,
  withAdmin2FA
};